import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEventsRsvpsV2RsvpManagementUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/events-rsvps',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/events/v2/rsvps',
        destPath: '/v2/rsvps',
      },
      {
        srcPath: '/events/v2/bulk/rsvps',
        destPath: '/v2/bulk/rsvps',
      },
    ],
    _: [
      {
        srcPath: '/_api/events-rsvps',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/events-rsvps',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/events-rsvps',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/events-rsvps',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/events-rsvps',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_events_rsvp-v-2';

/**
 * Retrieves a list of RSVPs that match the specified search query and optionally performs aggregations on the data queried.
 *
 * To learn about working with _search_, see [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language), [Sorting and Paging](https://dev.wix.com/docs/rest/articles/get-started/sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/rest/articles/get-started/field-projection).
 */
export function searchRsvps(payload: object): RequestOptionsFactory<any> {
  function __searchRsvps({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'search.aggregations.range.buckets.from' },
          { path: 'search.aggregations.range.buckets.to' },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.from',
          },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.to',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.v2.rsvp',
      method: 'POST' as any,
      methodFqn: 'wix.events.rsvps.v2.RsvpManagement.SearchRsvps',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsRsvpsV2RsvpManagementUrl({
        protoPath: '/v2/rsvps/search',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'rsvps.createdDate' },
              { path: 'rsvps.updatedDate' },
              { path: 'rsvps.form.inputValues.dateTime' },
              { path: 'rsvps.guestList.guests.checkInDetails.checkInDate' },
              {
                path: 'rsvps.guestList.guests.checkInDetails.checkInCanceledDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'rsvps.form.inputValues.number' },
              {
                path: 'rsvps.form.inputValues.address.address.geocode.latitude',
              },
              {
                path: 'rsvps.form.inputValues.address.address.geocode.longitude',
              },
              { path: 'aggregationData.results.ranges.results.from' },
              { path: 'aggregationData.results.ranges.results.to' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.from',
              },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.to',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.from',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.to',
              },
              { path: 'aggregationData.results.scalar.value' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.scalar.value',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.scalar.value',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __searchRsvps;
}

/**
 * Creates a query to retrieve a list of RSVPs.
 *
 * The `queryRsvps()` method builds a query to retrieve a list of RSVPs and returns an `RsvpsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `RsvpsQueryBuilder` methods onto the query. `RsvpsQueryBuilder` methods enable you to sort, filter, and control the results `queryRsvps()` returns.
 *
 * `queryRsvps()` runs with these `RsvpsQueryBuilder` defaults, which you can override:
 *
 * - limit(50)
 * - descending("_createdDate")
 *
 * The methods that are chained to `queryRsvps()` are applied in the order they're called. For example, if you apply ascending('status') and then descending('eventId'), the results are sorted first by the status, and then, if there are multiple results with the same status, the items are sorted by event ID.
 *
 * **Note:** This method is quite slow. We suggest you using the following filter pairs for the most optimal speed:
 * - `eventId` and `status`
 * - `eventId` and `fullyCheckedIn`
 * - `eventId` and `memberId`
 * - `eventId` and `contactId`
 * - `eventId` and `createdDate`
 */
export function queryRsvps(payload: object): RequestOptionsFactory<any> {
  function __queryRsvps({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v2.rsvp',
      method: 'POST' as any,
      methodFqn: 'wix.events.rsvps.v2.RsvpManagement.QueryRsvps',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsRsvpsV2RsvpManagementUrl({
        protoPath: '/v2/rsvps/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'rsvps.createdDate' },
              { path: 'rsvps.updatedDate' },
              { path: 'rsvps.form.inputValues.dateTime' },
              { path: 'rsvps.guestList.guests.checkInDetails.checkInDate' },
              {
                path: 'rsvps.guestList.guests.checkInDetails.checkInCanceledDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'rsvps.form.inputValues.number' },
              {
                path: 'rsvps.form.inputValues.address.address.geocode.latitude',
              },
              {
                path: 'rsvps.form.inputValues.address.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryRsvps;
}

/** Counts RSVPs with the specified filtering and searching. */
export function countRsvps(payload: object): RequestOptionsFactory<any> {
  function __countRsvps({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v2.rsvp',
      method: 'POST' as any,
      methodFqn: 'wix.events.rsvps.v2.RsvpManagement.CountRsvps',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsRsvpsV2RsvpManagementUrl({
        protoPath: '/v2/rsvps/count',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __countRsvps;
}

/** Retrieves an RSVP by ID. */
export function getRsvp(payload: object): RequestOptionsFactory<any> {
  function __getRsvp({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v2.rsvp',
      method: 'GET' as any,
      methodFqn: 'wix.events.rsvps.v2.RsvpManagement.GetRsvp',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsRsvpsV2RsvpManagementUrl({
        protoPath: '/v2/rsvps/{rsvpId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'rsvp.createdDate' },
              { path: 'rsvp.updatedDate' },
              { path: 'rsvp.form.inputValues.dateTime' },
              { path: 'rsvp.guestList.guests.checkInDetails.checkInDate' },
              {
                path: 'rsvp.guestList.guests.checkInDetails.checkInCanceledDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'rsvp.form.inputValues.number' },
              {
                path: 'rsvp.form.inputValues.address.address.geocode.latitude',
              },
              {
                path: 'rsvp.form.inputValues.address.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getRsvp;
}

/**
 * Creates an RSVP.
 *
 * To create anRSVP with multiple guests, you have to specify the `additionalGuests` and `guestNames` fields.
 */
export function createRsvp(payload: object): RequestOptionsFactory<any> {
  function __createRsvp({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'rsvp.createdDate' },
          { path: 'rsvp.updatedDate' },
          { path: 'rsvp.form.inputValues.dateTime' },
          { path: 'rsvp.guestList.guests.checkInDetails.checkInDate' },
          { path: 'rsvp.guestList.guests.checkInDetails.checkInCanceledDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'rsvp.form.inputValues.number' },
          { path: 'rsvp.form.inputValues.address.address.geocode.latitude' },
          { path: 'rsvp.form.inputValues.address.address.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.v2.rsvp',
      method: 'POST' as any,
      methodFqn: 'wix.events.rsvps.v2.RsvpManagement.CreateRsvp',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsRsvpsV2RsvpManagementUrl({
        protoPath: '/v2/rsvps',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'rsvp.createdDate' },
              { path: 'rsvp.updatedDate' },
              { path: 'rsvp.form.inputValues.dateTime' },
              { path: 'rsvp.guestList.guests.checkInDetails.checkInDate' },
              {
                path: 'rsvp.guestList.guests.checkInDetails.checkInCanceledDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'rsvp.form.inputValues.number' },
              {
                path: 'rsvp.form.inputValues.address.address.geocode.latitude',
              },
              {
                path: 'rsvp.form.inputValues.address.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createRsvp;
}

/**
 * Updates RSVP.
 *
 * Each time the RSVP is updated, `revision` increments by 1. The existing `revision` must be specified when updating the RSVP. This ensures you're working with the latest RSVP and prevents unintended overwrites.
 */
export function updateRsvp(payload: object): RequestOptionsFactory<any> {
  function __updateRsvp({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'rsvp.createdDate' },
          { path: 'rsvp.updatedDate' },
          { path: 'rsvp.form.inputValues.dateTime' },
          { path: 'rsvp.guestList.guests.checkInDetails.checkInDate' },
          { path: 'rsvp.guestList.guests.checkInDetails.checkInCanceledDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'rsvp.form.inputValues.number' },
          { path: 'rsvp.form.inputValues.address.address.geocode.latitude' },
          { path: 'rsvp.form.inputValues.address.address.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.v2.rsvp',
      method: 'PATCH' as any,
      methodFqn: 'wix.events.rsvps.v2.RsvpManagement.UpdateRsvp',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsRsvpsV2RsvpManagementUrl({
        protoPath: '/v2/rsvps/{rsvp.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'rsvp.createdDate' },
              { path: 'rsvp.updatedDate' },
              { path: 'rsvp.form.inputValues.dateTime' },
              { path: 'rsvp.guestList.guests.checkInDetails.checkInDate' },
              {
                path: 'rsvp.guestList.guests.checkInDetails.checkInCanceledDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'rsvp.form.inputValues.number' },
              {
                path: 'rsvp.form.inputValues.address.address.geocode.latitude',
              },
              {
                path: 'rsvp.form.inputValues.address.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateRsvp;
}

/** Deletes an RSVP. */
export function deleteRsvp(payload: object): RequestOptionsFactory<any> {
  function __deleteRsvp({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v2.rsvp',
      method: 'DELETE' as any,
      methodFqn: 'wix.events.rsvps.v2.RsvpManagement.DeleteRsvp',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsRsvpsV2RsvpManagementUrl({
        protoPath: '/v2/rsvps/{rsvpId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteRsvp;
}

/**
 * Updates multiple RSVPs.
 *
 * You can update up to 100 RSVPs per request.
 */
export function bulkUpdateRsvps(payload: object): RequestOptionsFactory<any> {
  function __bulkUpdateRsvps({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'rsvps.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'rsvps.rsvp.createdDate' },
          { path: 'rsvps.rsvp.updatedDate' },
          { path: 'rsvps.rsvp.form.inputValues.dateTime' },
          { path: 'rsvps.rsvp.guestList.guests.checkInDetails.checkInDate' },
          {
            path: 'rsvps.rsvp.guestList.guests.checkInDetails.checkInCanceledDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'rsvps.rsvp.form.inputValues.number' },
          {
            path: 'rsvps.rsvp.form.inputValues.address.address.geocode.latitude',
          },
          {
            path: 'rsvps.rsvp.form.inputValues.address.address.geocode.longitude',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.v2.rsvp',
      method: 'PATCH' as any,
      methodFqn: 'wix.events.rsvps.v2.RsvpManagement.BulkUpdateRsvps',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsRsvpsV2RsvpManagementUrl({
        protoPath: '/v2/bulk/rsvps/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.rsvp.createdDate' },
              { path: 'results.rsvp.updatedDate' },
              { path: 'results.rsvp.form.inputValues.dateTime' },
              {
                path: 'results.rsvp.guestList.guests.checkInDetails.checkInDate',
              },
              {
                path: 'results.rsvp.guestList.guests.checkInDetails.checkInCanceledDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.rsvp.form.inputValues.number' },
              {
                path: 'results.rsvp.form.inputValues.address.address.geocode.latitude',
              },
              {
                path: 'results.rsvp.form.inputValues.address.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateRsvps;
}

/** Deletes multiple RSVPs. */
export function bulkDeleteRsvpsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteRsvpsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v2.rsvp',
      method: 'POST' as any,
      methodFqn: 'wix.events.rsvps.v2.RsvpManagement.BulkDeleteRsvpsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsRsvpsV2RsvpManagementUrl({
        protoPath: '/v2/bulk/rsvps/delete-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteRsvpsByFilter;
}

/**
 * Checks in RSVP guests.
 *
 * You can check in up to 11 guests per request.
 */
export function checkInRsvpGuests(payload: object): RequestOptionsFactory<any> {
  function __checkInRsvpGuests({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v2.rsvp',
      method: 'POST' as any,
      methodFqn: 'wix.events.rsvps.v2.RsvpManagement.CheckInRsvpGuests',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsRsvpsV2RsvpManagementUrl({
        protoPath: '/v2/rsvps/{rsvpId}/check-in',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'rsvp.createdDate' },
              { path: 'rsvp.updatedDate' },
              { path: 'rsvp.form.inputValues.dateTime' },
              { path: 'rsvp.guestList.guests.checkInDetails.checkInDate' },
              {
                path: 'rsvp.guestList.guests.checkInDetails.checkInCanceledDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'rsvp.form.inputValues.number' },
              {
                path: 'rsvp.form.inputValues.address.address.geocode.latitude',
              },
              {
                path: 'rsvp.form.inputValues.address.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __checkInRsvpGuests;
}

/**
 * Cancels the check-in of RSVP guests.
 *
 * If the request body is empty, cancels all check-ins from the RSVP. After the cancellation, the `guests.checkInDate` value becomes empty.
 *
 * You can cancel the check-in of up to 11 guests per request.
 */
export function cancelRsvpGuestsCheckIn(
  payload: object
): RequestOptionsFactory<any> {
  function __cancelRsvpGuestsCheckIn({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v2.rsvp',
      method: 'POST' as any,
      methodFqn: 'wix.events.rsvps.v2.RsvpManagement.CancelRsvpGuestsCheckIn',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsRsvpsV2RsvpManagementUrl({
        protoPath: '/v2/rsvps/{rsvpId}/cancel-check-in',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'rsvp.createdDate' },
              { path: 'rsvp.updatedDate' },
              { path: 'rsvp.form.inputValues.dateTime' },
              { path: 'rsvp.guestList.guests.checkInDetails.checkInDate' },
              {
                path: 'rsvp.guestList.guests.checkInDetails.checkInCanceledDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'rsvp.form.inputValues.number' },
              {
                path: 'rsvp.form.inputValues.address.address.geocode.latitude',
              },
              {
                path: 'rsvp.form.inputValues.address.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __cancelRsvpGuestsCheckIn;
}

/** Retrieves an RSVP summary. */
export function listRsvpSummary(payload: object): RequestOptionsFactory<any> {
  function __listRsvpSummary({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v2.rsvp',
      method: 'GET' as any,
      methodFqn: 'wix.events.rsvps.v2.RsvpManagement.ListRsvpSummary',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsRsvpsV2RsvpManagementUrl({
        protoPath: '/v2/rsvps/summaries',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listRsvpSummary;
}

/** Lists RSVPs for the authenticated member across specified events. */
export function listCurrentRsvps(payload: object): RequestOptionsFactory<any> {
  function __listCurrentRsvps({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v2.rsvp',
      method: 'GET' as any,
      methodFqn: 'wix.events.rsvps.v2.RsvpManagement.ListCurrentRsvps',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsRsvpsV2RsvpManagementUrl({
        protoPath: '/v2/rsvps/current',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'rsvps.createdDate' },
              { path: 'rsvps.updatedDate' },
              { path: 'rsvps.form.inputValues.dateTime' },
              { path: 'rsvps.guestList.guests.checkInDetails.checkInDate' },
              {
                path: 'rsvps.guestList.guests.checkInDetails.checkInCanceledDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'rsvps.form.inputValues.number' },
              {
                path: 'rsvps.form.inputValues.address.address.geocode.latitude',
              },
              {
                path: 'rsvps.form.inputValues.address.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listCurrentRsvps;
}
